/**
 * Stage 2 decisions — the external sources the stance rules cite.
 *
 * Reviewed 28 September 2026 against the primary page where it was reachable
 * (the notes say when a secondary report carried the finding); the EPA
 * nonattainment-area boundary service was added and reviewed on 30 September
 * 2026 (EPA-7). A rule that
 * turns one of these findings into a stance cites its id. A threshold that is
 * platform judgement says so with `basis: 'screening-policy'` instead of
 * borrowing an authority it does not have.
 */

export type Stage2SourceId =
  | 'cfr-40-51-165'
  | 'epa-green-book-data'
  | 'epa-nonattainment-boundaries'
  | 'usc-42-7511c'
  | 'epa-virginia-otr-2018'
  | 'wri-aqueduct-4'
  | 'microsoft-zero-water-cooling'
  | 'nrel-water-factors-2011'
  | 'lbnl-queued-up-2026'
  | 'ge-vernova-lead-times-2026'
  | 'doe-clinch-river-smr-2025'
  | 'eia-vogtle-2024'
  | 'lbnl-pv-land-2022'
  | 'nrel-wind-land-2009'
  | 'platform-workload-profiles';

export interface Stage2Source {
  id: Stage2SourceId;
  title: string;
  publisher: string;
  /** Empty for platform-internal assumptions. */
  url: string;
  /** The finding a rule takes from the source, in one sentence. */
  finding: string;
  reviewedAt: '2026-09-28' | '2026-09-30';
}

export const STAGE2_SOURCES: Readonly<Record<Stage2SourceId, Stage2Source>> = {
  'cfr-40-51-165': {
    id: 'cfr-40-51-165',
    title: '40 CFR 51.165 — Permit requirements (nonattainment NSR)',
    publisher: 'US Code of Federal Regulations (Cornell LII)',
    url: 'https://www.law.cornell.edu/cfr/text/40/51.165',
    finding: 'In a nonattainment area, a new source that is major for the designated pollutant needs preconstruction review satisfying Clean Air Act §173 (emission offsets and the lowest achievable emission rate) ((a)(2)(i)); it is major at 100 tpy of that pollutant unless a lower threshold applies, such as 70 tpy of PM-10 or PM2.5 in a serious area ((a)(1)(iv)(A)(1), (vi) and (vii)). Ozone nonattainment major-source thresholds for NOx or VOC are 100 tpy (marginal, moderate), 50 tpy (serious), 25 tpy (severe) and 10 tpy (extreme); emission offsets are at least 1.1, 1.15, 1.2, 1.3 and 1.5 to 1 respectively. In an ozone transport region outside serious, severe and extreme areas, attainment and unclassifiable areas included, VOC is major at 50 tpy and NOx at 100 tpy, and offsets are at least 1.15:1 (paragraphs (a)(1)(iv)(A)(1)(ii), (a)(1)(iv)(A)(2)(iii) and (a)(9)(iii)).',
    reviewedAt: '2026-09-28',
  },
  'epa-green-book-data': {
    id: 'epa-green-book-data',
    title: 'Green Book data download (NAYRO county export)',
    publisher: 'US EPA',
    url: 'https://www.epa.gov/green-book/green-book-data-download',
    finding: 'EPA publishes county-level nonattainment and maintenance designations for each standard, including ozone classification. The platform table (sed.environmental.naaqsDesignations) is built from the NAYRO export of 31 August 2026, downloaded 28 September 2026, with revoked standards excluded.',
    reviewedAt: '2026-09-28',
  },
  'epa-nonattainment-boundaries': {
    id: 'epa-nonattainment-boundaries',
    title: 'Nonattainment Areas and Designations, United States (NonattainmentAreas MapServer)',
    publisher: 'US EPA Office of Air and Radiation (OAQPS)',
    url: 'https://gispub.epa.gov/arcgis/rest/services/OAR_OAQPS/NonattainmentAreas/MapServer',
    finding: 'EPA publishes the nonattainment and maintenance area boundaries as one polygon layer per standard, updated weekly from an OAQPS database. The platform queries the layer for each part-county nonattainment area at the site point and reports inside only when a polygon containing the point carries the Green Book area\'s name and state, and outside only when the layer holds that area\'s polygon and the point is not in it; 40 CFR Part 81 remains the legal boundary.',
    reviewedAt: '2026-09-30',
  },
  'usc-42-7511c': {
    id: 'usc-42-7511c',
    title: '42 U.S.C. 7511c (Clean Air Act §184) — Control of interstate ozone air pollution',
    publisher: 'US Code (Cornell LII)',
    url: 'https://www.law.cornell.edu/uscode/text/42/7511c',
    finding: 'The Ozone Transport Region is Connecticut, Delaware, Maine, Maryland, Massachusetts, New Hampshire, New Jersey, New York, Pennsylvania, Rhode Island, Vermont and the Consolidated Metropolitan Statistical Area that includes the District of Columbia (§184(a)); a source there that emits or could emit at least 50 tpy of VOC is a major source subject to the requirements of a moderate nonattainment area (§184(b)(2)).',
    reviewedAt: '2026-09-28',
  },
  'epa-virginia-otr-2018': {
    id: 'epa-virginia-otr-2018',
    title: 'Virginia; Nonattainment New Source Review Requirements for the 2008 8-Hour Ozone Standard (83 FR 45351, 7 September 2018)',
    publisher: 'US EPA (Federal Register)',
    url: 'https://www.govinfo.gov/content/pkg/FR-2018-09-07/html/2018-19364.htm',
    finding: 'EPA lists the Virginia portion of the Ozone Transport Region as Arlington, Fairfax, Loudoun, Prince William and Stafford counties and the cities of Alexandria, Fairfax, Falls Church, Manassas and Manassas Park.',
    reviewedAt: '2026-09-28',
  },
  'wri-aqueduct-4': {
    id: 'wri-aqueduct-4',
    title: 'Aqueduct 4.0 baseline water stress',
    publisher: 'World Resources Institute',
    url: 'https://www.wri.org/research/aqueduct-40-updated-decision-relevant-global-water-risk-indicators',
    finding: 'Baseline water stress runs from low (<10% of available supply withdrawn) to extremely high (>80%); high is 40–80%. WRI leaves decision thresholds to the user and calls for site-specific confirmation.',
    reviewedAt: '2026-09-28',
  },
  'microsoft-zero-water-cooling': {
    id: 'microsoft-zero-water-cooling',
    title: 'Next-generation datacenters consume zero water for cooling (9 December 2024)',
    publisher: 'Microsoft',
    url: 'https://www.microsoft.com/en-us/microsoft-cloud/blog/2024/12/09/sustainable-by-design-next-generation-datacenters-consume-zero-water-for-cooling/',
    finding: 'Since August 2024 Microsoft designs AI datacenters with closed-loop chip-level cooling that evaporates no water, avoiding more than 125 million litres a year per datacenter.',
    reviewedAt: '2026-09-28',
  },
  'nrel-water-factors-2011': {
    id: 'nrel-water-factors-2011',
    title: 'Operational water consumption and withdrawal factors (NREL/TP-6A20-50900), Tables 2–3',
    publisher: 'NREL (Macknick et al.)',
    url: 'https://docs.nlr.gov/docs/fy11osti/50900.pdf',
    finding: 'Median gas combined-cycle water consumption is 198 gal/MWh with cooling towers and 2 gal/MWh dry-cooled; once-through withdrawal is 11,380 gal/MWh against 253 gal/MWh for towers.',
    reviewedAt: '2026-09-28',
  },
  'lbnl-queued-up-2026': {
    id: 'lbnl-queued-up-2026',
    title: 'Queued Up: 2026 Edition (reported by APPA, 1 July 2026)',
    publisher: 'Lawrence Berkeley National Laboratory',
    url: 'https://emp.lbl.gov/publications/queued-2026-edition-characteristics',
    finding: 'For regions with data, generator interconnection took over five years from request to commercial operation; only 13% of capacity requested in 2000–2020 was online by the end of 2025.',
    reviewedAt: '2026-09-28',
  },
  'ge-vernova-lead-times-2026': {
    id: 'ge-vernova-lead-times-2026',
    title: 'GE Vernova order and lead-time update (Power Engineering, 23 April 2026)',
    publisher: 'Power Engineering',
    url: 'https://www.power-eng.com/gas/turbines/data-centers-drive-record-surge-in-ge-vernova-power-equipment-orders-as-turbine-slots-tighten-through-2030/',
    finding: 'Directional lead times on new heavy-duty gas turbine orders are about three years; permitting and fuel availability often extend total project timelines beyond that.',
    reviewedAt: '2026-09-28',
  },
  'doe-clinch-river-smr-2025': {
    id: 'doe-clinch-river-smr-2025',
    title: 'NRC dockets construction permit application for TVA small modular reactor (10 July 2025)',
    publisher: 'US Department of Energy',
    url: 'https://www.energy.gov/ne/articles/nrc-dockets-construction-permit-application-tva-small-modular-reactor',
    finding: 'The first US utility-scale SMR (TVA BWRX-300, Clinch River) applied for its construction permit in 2025; the first Canadian BWRX-300 expects commercial operation by the end of 2029.',
    reviewedAt: '2026-09-28',
  },
  'eia-vogtle-2024': {
    id: 'eia-vogtle-2024',
    title: 'Plant Vogtle Unit 4 begins commercial operation',
    publisher: 'US Energy Information Administration',
    url: 'https://www.eia.gov/todayinenergy/detail.php?id=61963',
    finding: 'Vogtle Unit 4 entered commercial operation on 29 April 2024 (Unit 3 on 31 July 2023), completing an expansion whose construction began in 2013.',
    reviewedAt: '2026-09-28',
  },
  'lbnl-pv-land-2022': {
    id: 'lbnl-pv-land-2022',
    title: 'Land Requirements for Utility-Scale PV (Bolinger & Bolinger, 2022)',
    publisher: 'Lawrence Berkeley National Laboratory',
    url: 'https://emp.lbl.gov/publications/land-requirements-utility-scale-pv',
    finding: 'Median utility PV power density is 0.28 MWac/acre fixed-tilt and 0.18 MWac/acre tracking; energy density is about 447 and 394 MWh/acre/year.',
    reviewedAt: '2026-09-28',
  },
  'nrel-wind-land-2009': {
    id: 'nrel-wind-land-2009',
    title: 'Land-Use Requirements of Modern Wind Power Plants in the US (NREL/TP-6A2-45834)',
    publisher: 'NREL (Denholm et al.)',
    url: 'https://www.osti.gov/biblio/964608',
    finding: 'Large US wind plants average about 34 ha (≈84 acres) of total project area per MW, of which about 1 ha/MW is directly disturbed.',
    reviewedAt: '2026-09-28',
  },
  'platform-workload-profiles': {
    id: 'platform-workload-profiles',
    title: 'Workload density anchors (src/lib/workloadProfile.ts)',
    publisher: 'Platform planning assumptions',
    url: '',
    finding: 'AI training is planned at 50–100 kW/rack with liquid (CDU) cooling; AI inference at 15–25 kW/rack with hybrid cooling; HPC at 30–50 kW/rack.',
    reviewedAt: '2026-09-28',
  },
};

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
] as const;

/**
 * A set of calendar review dates ('YYYY-MM-DD') as the decisions panel states
 * them: "28 September 2026" for one day, "28–30 September 2026" within a
 * month, "28 September – 2 October 2026" across months, and with both years
 * across a year end. Read as calendar dates, never through a time zone, so no
 * locale shifts a day. Empty input gives ''.
 */
export function formatStage2ReviewedRange(isoDates: readonly string[]): string {
  const sorted = [...new Set(isoDates)].sort();
  if (sorted.length === 0) return '';
  const parts = (iso: string) => {
    const [year, month, day] = iso.split('-').map(Number);
    return { day: day!, month: MONTH_NAMES[month! - 1]!, year: year! };
  };
  const first = parts(sorted[0]!);
  const last = parts(sorted[sorted.length - 1]!);
  if (sorted.length === 1) return `${first.day} ${first.month} ${first.year}`;
  if (first.year !== last.year) return `${first.day} ${first.month} ${first.year} – ${last.day} ${last.month} ${last.year}`;
  if (first.month !== last.month) return `${first.day} ${first.month} – ${last.day} ${last.month} ${last.year}`;
  return `${first.day}–${last.day} ${last.month} ${last.year}`;
}

/**
 * When the sources the stance rules cite were reviewed, for the decisions
 * panel footer ("28–30 September 2026"). Derived from `reviewedAt`, so a
 * source reviewed on a new day (EPA-7 added one on 30 September 2026) cannot
 * leave the footer claiming an older date.
 */
export const STAGE2_SOURCES_REVIEWED: string = formatStage2ReviewedRange(
  Object.values(STAGE2_SOURCES).map(source => source.reviewedAt),
);
